import React, { useState, useEffect } from "react";
import { 
  Card, 
  Row, 
  Col, 
  Statistic, 
  Alert, 
  Typography, 
  Space, 
  Tag, 
  Table, 
  Tabs,
  Progress,
  Skeleton,
  Badge,
  Tooltip
} from "antd";
import { 
  WarningOutlined,
  UserDeleteOutlined,
  RiseOutlined,
  FallOutlined,
  MinusOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { ANALYTICS_API_URL } from "../../../constants/config";
import { getAndSanitize } from "../../../shared/utils/sanitization";
import { useAnalytics } from "../../../contexts/AnalyticsContext";
import { ChurnAnalytics as ChurnAnalyticsType } from "../../../types/analytics";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface ChurnAnalyticsProps {
  loading?: boolean;
}

export const ChurnAnalytics: React.FC<ChurnAnalyticsProps> = ({ loading: propLoading }) => {
  const [data, setData] = useState<ChurnAnalyticsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const { filters } = useAnalytics();

  useEffect(() => {
    const fetchChurnAnalytics = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = getAndSanitize("token");
        const adminId = getAndSanitize("adminId");

        if (!token || !adminId) {
          setError("Token o adminId no disponible");
          return;
        }

        const queryParams = new URLSearchParams({
          adminId,
          'dateRange[start]': filters.dateRange.start,
          'dateRange[end]': filters.dateRange.end,
          ...(filters.accountName && { accountName: filters.accountName }),
        });

        const response = await fetch(`${ANALYTICS_API_URL}/api/analytics/churn?${queryParams}`, {
          method: "GET",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json",
            "X-Admin-ID": adminId,
          },
        });

        if (!response.ok) {
          throw new Error(`Error ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        
        if (result.success && result.data) {
          setData(result.data);
          setLastUpdated(new Date());
        } else {
          setError(result.message || "Error al obtener datos de churn");
        }
      } catch (err) {
        console.error("Error fetching churn analytics:", err);
        setError(err instanceof Error ? err.message : "Error al cargar analytics de churn");
      } finally {
        setLoading(false);
      }
    };

    fetchChurnAnalytics();
  }, [filters]);

  const getRiskColor = (riskScore: number): string => {
    if (riskScore >= 80) return '#ff4d4f';  // Alto riesgo - rojo
    if (riskScore >= 60) return '#fa8c16';  // Medio-alto - naranja
    if (riskScore >= 40) return '#faad14';  // Medio - amarillo
    if (riskScore >= 20) return '#52c41a';  // Bajo - verde
    return '#1890ff';                       // Muy bajo - azul
  };

  const getRiskBadge = (riskScore: number) => {
    if (riskScore >= 80) return <Badge status="error" text="Alto Riesgo" />;
    if (riskScore >= 60) return <Badge status="warning" text="Riesgo Medio-Alto" />;
    if (riskScore >= 40) return <Badge status="processing" text="Riesgo Medio" />;
    if (riskScore >= 20) return <Badge status="success" text="Riesgo Bajo" />;
    return <Badge status="default" text="Muy Bajo Riesgo" />;
  };

  const getTrendIcon = (trend: 'increasing' | 'decreasing' | 'stable') => {
    switch (trend) {
      case 'increasing':
        return <RiseOutlined style={{ color: '#52c41a' }} />;
      case 'decreasing':
        return <FallOutlined style={{ color: '#ff4d4f' }} />;
      default:
        return <MinusOutlined style={{ color: '#faad14' }} />;
    }
  };

  // Columnas para la tabla de usuarios en riesgo
  const riskUsersColumns = [
    {
      title: 'Usuario',
      dataIndex: 'username',
      key: 'username',
      render: (text: string) => <Text strong>{text}</Text>
    },
    {
      title: 'Cuenta',
      dataIndex: 'accountName',
      key: 'accountName',
      render: (text: string) => <Tag color="blue">{text || 'N/A'}</Tag>
    },
    {
      title: 'Riesgo',
      dataIndex: 'riskScore',
      key: 'riskScore',
      render: (score: number) => (
        <div>
          <Progress 
            percent={score} 
            size="small" 
            strokeColor={getRiskColor(score)}
            format={() => `${score}%`}
          />
          <div style={{ marginTop: 4 }}>
            {getRiskBadge(score)}
          </div>
        </div>
      ),
      sorter: (a: any, b: any) => b.riskScore - a.riskScore
    },
    {
      title: 'Días sin Actividad',
      dataIndex: 'daysSinceLastLoad',
      key: 'daysSinceLastLoad',
      render: (days: number) => (
        <Text type={days > 60 ? 'danger' : days > 30 ? 'warning' : 'secondary'}>
          {days} días
        </Text>
      ),
      sorter: (a: any, b: any) => b.daysSinceLastLoad - a.daysSinceLastLoad
    },
    {
      title: 'Tendencia',
      dataIndex: 'loadFrequencyTrend',
      key: 'loadFrequencyTrend',
      render: (trend: 'increasing' | 'decreasing' | 'stable') => (
        <span>
          {getTrendIcon(trend)}
          <Text style={{ marginLeft: 8 }}>
            {trend === 'increasing' ? 'Creciente' : 
             trend === 'decreasing' ? 'Decreciente' : 'Estable'}
          </Text>
        </span>
      )
    },
    {
      title: 'Valor en Riesgo',
      dataIndex: 'expectedValue',
      key: 'expectedValue',
      render: (value: number) => (
        <Text strong style={{ color: '#ff4d4f' }}>
          ${value.toLocaleString()}
        </Text>
      ),
      sorter: (a: any, b: any) => b.expectedValue - a.expectedValue
    },
    {
      title: 'Acción Sugerida',
      dataIndex: 'suggestedAction',
      key: 'suggestedAction',
      render: (action: string) => (
        <Tooltip title={action}>
          <Text ellipsis style={{ maxWidth: 150 }}>
            {action}
          </Text>
        </Tooltip>
      )
    }
  ];

  // Columnas para factores de churn
  const churnFactorsColumns = [
    {
      title: 'Factor',
      dataIndex: 'factor',
      key: 'factor',
      render: (factor: string) => {
        const factorNames: Record<string, string> = {
          'high_inactivity': '🕒 Alta Inactividad',
          'declining_frequency': '📉 Frecuencia Decreciente',
          'no_recent_activity': '❌ Sin Actividad Reciente',
          'low_engagement': '⚠️ Bajo Engagement'
        };
        return factorNames[factor] || factor;
      }
    },
    {
      title: 'Impacto',
      dataIndex: 'impact',
      key: 'impact',
      render: (impact: number) => (
        <Progress 
          percent={impact * 100} 
          size="small" 
          strokeColor={impact > 0.7 ? '#ff4d4f' : impact > 0.5 ? '#faad14' : '#52c41a'}
          format={() => `${(impact * 100).toFixed(0)}%`}
        />
      ),
      sorter: (a: any, b: any) => b.impact - a.impact
    },
    {
      title: 'Frecuencia',
      dataIndex: 'frequency',
      key: 'frequency',
      render: (freq: number) => <Text strong>{freq} usuarios</Text>,
      sorter: (a: any, b: any) => b.frequency - a.frequency
    },
    {
      title: 'Descripción',
      dataIndex: 'description',
      key: 'description'
    }
  ];

  if (loading || propLoading) {
    return (
      <div style={{ padding: 24 }}>
        <Skeleton active />
        <Skeleton active />
        <Skeleton active />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error al cargar Análisis de Churn"
        description={error}
        type="error"
        showIcon
        style={{ margin: 24 }}
      />
    );
  }

  if (!data) {
    return (
      <Alert
        message="Sin datos disponibles"
        description="No hay suficientes datos para generar el análisis de churn"
        type="info"
        showIcon
        style={{ margin: 24 }}
      />
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={3}>⚠️ Análisis de Churn y Riesgo</Title>
          <Text type="secondary">
            Identificación de usuarios en riesgo de abandono y factores de churn
          </Text>
        </div>

        {/* Métricas Principales */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Usuarios en Alto Riesgo"
                value={data.churnMetrics.totalUsersAtRisk}
                prefix={<WarningOutlined style={{ color: '#ff4d4f' }} />}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Valor en Riesgo"
                value={data.churnMetrics.totalValueAtRisk}
                prefix="$"
                precision={0}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Tasa de Churn"
                value={data.churnMetrics.churnRate}
                suffix="%"
                precision={1}
                prefix={<UserDeleteOutlined />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card>
              <Statistic
                title="Oportunidades de Reactivación"
                value={data.churnMetrics.reactivationOpportunities}
                prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Distribución de Riesgo */}
        <Card title="📊 Distribución de Riesgo por Niveles">
          <Row gutter={[16, 16]}>
            {[
              { range: '80-100%', label: 'Alto Riesgo', color: '#ff4d4f' },
              { range: '60-79%', label: 'Riesgo Medio-Alto', color: '#fa8c16' },
              { range: '40-59%', label: 'Riesgo Medio', color: '#faad14' },
              { range: '20-39%', label: 'Riesgo Bajo', color: '#52c41a' },
              { range: '0-19%', label: 'Muy Bajo Riesgo', color: '#1890ff' }
            ].map((level, index) => {
              const count = data.churnRiskUsers.filter(user => {
                const ranges = [
                  [80, 100], [60, 79], [40, 59], [20, 39], [0, 19]
                ];
                const [min, max] = ranges[index];
                return user.riskScore >= min && user.riskScore <= max;
              }).length;

              return (
                <Col xs={24} sm={12} lg={4} key={level.range}>
                  <Card size="small" style={{ textAlign: 'center' }}>
                    <div style={{ 
                      backgroundColor: level.color, 
                      color: 'white', 
                      padding: '8px', 
                      borderRadius: '4px',
                      marginBottom: '8px'
                    }}>
                      <Text strong style={{ color: 'white' }}>{level.range}</Text>
                    </div>
                    <Statistic
                      title={level.label}
                      value={count}
                      valueStyle={{ fontSize: '20px', color: level.color }}
                    />
                  </Card>
                </Col>
              );
            })}
          </Row>
        </Card>

        {/* Tabs con datos detallados */}
        <Tabs defaultActiveKey="riskUsers" size="large">
          <TabPane tab="👥 Usuarios en Riesgo" key="riskUsers">
            <Card title={`Usuarios en Riesgo (${data.churnRiskUsers.length} total)`}>
              <Table
                dataSource={data.churnRiskUsers}
                columns={riskUsersColumns}
                rowKey="username"
                pagination={{ 
                  pageSize: 15,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => 
                    `${range[0]}-${range[1]} de ${total} usuarios`
                }}
                scroll={{ x: 1200 }}
              />
            </Card>
          </TabPane>

          <TabPane tab="🔍 Factores de Churn" key="factors">
            <Card title="Factores que Contribuyen al Churn">
              <Table
                dataSource={data.churnFactors}
                columns={churnFactorsColumns}
                rowKey="factor"
                pagination={false}
              />
            </Card>
          </TabPane>

          <TabPane tab="🎯 Candidatos para Reactivación" key="reactivation">
            <Card title={`Oportunidades de Reactivación (${data.reactivationCandidates.length} usuarios)`}>
              <Table
                dataSource={data.reactivationCandidates}
                columns={[
                  {
                    title: 'Usuario',
                    dataIndex: 'username',
                    key: 'username',
                    render: (text: string) => <Text strong>{text}</Text>
                  },
                  {
                    title: 'Días Inactivo',
                    dataIndex: 'daysSinceLastActivity',
                    key: 'daysSinceLastActivity',
                    render: (days: number) => (
                      <Badge 
                        count={days} 
                        style={{ backgroundColor: days > 60 ? '#ff4d4f' : '#faad14' }} 
                        showZero
                      />
                    ),
                    sorter: (a: any, b: any) => b.daysSinceLastActivity - a.daysSinceLastActivity
                  },
                  {
                    title: 'Incentivo Sugerido',
                    dataIndex: 'suggestedIncentive',
                    key: 'suggestedIncentive'
                  },
                  {
                    title: 'Probabilidad de Reactivación',
                    dataIndex: 'expectedReactivationRate',
                    key: 'expectedReactivationRate',
                    render: (rate: number) => (
                      <Progress 
                        percent={rate * 100} 
                        size="small" 
                        strokeColor="#52c41a"
                        format={() => `${(rate * 100).toFixed(0)}%`}
                      />
                    ),
                    sorter: (a: any, b: any) => b.expectedReactivationRate - a.expectedReactivationRate
                  },
                  {
                    title: 'Valor Potencial',
                    dataIndex: 'potentialValue',
                    key: 'potentialValue',
                    render: (value: number) => (
                      <Text strong style={{ color: '#52c41a' }}>
                        ${value.toLocaleString()}
                      </Text>
                    ),
                    sorter: (a: any, b: any) => b.potentialValue - a.potentialValue
                  }
                ]}
                rowKey="username"
                pagination={{ pageSize: 10 }}
              />
            </Card>
          </TabPane>
        </Tabs>

        {/* Footer con información */}
        <Card size="small">
          <Text type="secondary">
            📅 Última actualización: {lastUpdated.toLocaleString('es-ES')} | 
            💡 El análisis se basa en patrones de actividad y comportamiento de los usuarios
          </Text>
        </Card>
      </Space>
    </div>
  );
};

export default ChurnAnalytics; 